import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Maintenance, MaintenanceType, MaintenanceStatus, Priority } from '../models/maintenance';
import { ImmobilierService } from '../services/immobilier.service';
import { AddEditMaintenanceDialogComponent } from './add-edit-maintenance-dialog/add-edit-maintenance-dialog.component';
import { MaintenanceDetailDialogComponent } from './maintenance-detail-dialog/maintenance-detail-dialog.component';
import { ExcelService } from '../excel.service';
import { PdfService } from '../services/pdf.service';


@Component({
  selector: 'app-reparations-maintenance',
  templateUrl: './reparations-maintenance.component.html',
  styleUrls: ['./reparations-maintenance.component.css']
})
export class ReparationsMaintenanceComponent implements OnInit {

  maintenances: Maintenance[] = [];
  filteredMaintenances: Maintenance[] = [];
  properties: any[] = [];

  // Filtres
  searchTerm: string = '';
  selectedType: MaintenanceType | 'all' = 'all';
  selectedStatus: MaintenanceStatus | 'all' = 'all';
  selectedPriority: Priority | 'all' = 'all';
  selectedProperty: number | 'all' = 'all';

  startDate: Date | null = null;
  endDate: Date | null = null;

  // Pagination
  pageSize = 10;
  currentPage = 0;
  totalItems = 0;

  // Table
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  dataSource = new MatTableDataSource<Maintenance>();
  displayedColumns: string[] = [
    'reference',
    'propertyTitle',
    'type',
    'title',
    'priority',
    'status',
    'scheduledDate',
    'actualCost',
    'actions'
  ];

  // Types et statuts disponibles
  maintenanceTypes: MaintenanceType[] = [
    'reparation', 'entretien', 'renovation', 'inspection', 'urgence', 'maintenance_preventive'
  ];

  maintenanceStatuses: MaintenanceStatus[] = [
    'demande', 'planifiee', 'en_cours', 'terminee', 'annulee', 'reportee', 'en_attente_fournisseur'
  ];

  priorities: Priority[] = ['basse', 'normale', 'haute', 'urgence'];

  // UI
  showFilters = false;
  loading = false;

  // Statistiques
  stats: any = {};

  constructor(
    private immobilierService: ImmobilierService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private excelService: ExcelService,
    private pdfService: PdfService
  ) { }


  ngOnInit(): void {
    this.loadMaintenances();
    this.loadProperties();
  }

  loadMaintenances(): void {
    this.loading = true;
    this.immobilierService.getMaintenances().subscribe({
      next: (maintenances) => {
        this.maintenances = maintenances;
        this.filteredMaintenances = [...maintenances];
        this.updateDataSource();
        this.loading = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des maintenances:', error);
        this.snackBar.open('Erreur lors du chargement des maintenances', 'Fermer', {
          duration: 3000
        });
        this.loading = false;
      }
    });
  }

  loadProperties(): void {
    this.immobilierService.getProperties().subscribe({
      next: (properties) => {
        this.properties = properties;
      }
    });
  }

  calculateStatistics(): void {
    let enCours = 0;
    let terminees = 0;
    let urgentes = 0;

    this.filteredMaintenances.forEach(m => {
      // Pour "En Cours", comptabilisons ceux qui ne sont pas terminés ou annulés/reportés
      if (['demande', 'planifiee', 'en_cours', 'en_attente_fournisseur'].includes(m.status)) {
        enCours++;
      }
      if (m.status === 'terminee') {
        terminees++;
      }
      if (m.priority === 'urgence') {
        urgentes++;
      }
    });

    this.stats = {
      enCours,
      terminees,
      urgentes,
      total: this.filteredMaintenances.length
    };
  }

  applyFilters(): void {
    this.filteredMaintenances = this.maintenances.filter(maintenance => {
      // Recherche textuelle
      const matchesSearch = !this.searchTerm ||
        maintenance.title.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        maintenance.reference.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        maintenance.description.toLowerCase().includes(this.searchTerm.toLowerCase());

      // Filtre type
      const matchesType = this.selectedType === 'all' || maintenance.type === this.selectedType;

      // Filtre statut
      const matchesStatus = this.selectedStatus === 'all' || maintenance.status === this.selectedStatus;

      // Filtre priorité
      const matchesPriority = this.selectedPriority === 'all' || maintenance.priority === this.selectedPriority;

      // Filtre propriété
      const matchesProperty = this.selectedProperty === 'all' || maintenance.propertyId === this.selectedProperty;

      // Filtre date (scheduledDate)
      let matchesDate = true;
      if (this.startDate && this.endDate) {
        const maintenanceDate = new Date(maintenance.scheduledDate);
        matchesDate = maintenanceDate >= this.startDate && maintenanceDate <= this.endDate;
      } else if (this.startDate) {
        const maintenanceDate = new Date(maintenance.scheduledDate);
        matchesDate = maintenanceDate >= this.startDate;
      } else if (this.endDate) {
        const maintenanceDate = new Date(maintenance.scheduledDate);
        matchesDate = maintenanceDate <= this.endDate;
      }

      return matchesSearch && matchesType && matchesStatus && matchesPriority && matchesProperty && matchesDate;
    });

    this.updateDataSource();
  }

  updateDataSource(): void {
    this.dataSource.data = this.filteredMaintenances;
    this.totalItems = this.filteredMaintenances.length;

    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    this.calculateStatistics();
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  onPageChange(event: any): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
  }


  onFilterChange(): void {
    this.applyFilters();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.selectedType = 'all';
    this.selectedStatus = 'all';
    this.selectedPriority = 'all';
    this.selectedProperty = 'all';
    this.startDate = null;
    this.endDate = null;
    this.applyFilters();
  }

  onDateChange(): void {
    this.applyFilters();
  }

  addMaintenance(): void {
    const dialogRef = this.dialog.open(AddEditMaintenanceDialogComponent, {
      width: '800px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadMaintenances();
      }
    });
  }

  editMaintenance(maintenance: Maintenance): void {
    const dialogRef = this.dialog.open(AddEditMaintenanceDialogComponent, {
      width: '800px',
      disableClose: true,
      data: { maintenance: maintenance }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadMaintenances();
      }
    });
  }

  deleteMaintenance(maintenance: Maintenance): void {
    if (confirm(`Êtes-vous sûr de vouloir supprimer la maintenance ${maintenance.reference}?`)) {
      this.immobilierService.deleteMaintenance(maintenance.id).subscribe({
        next: (success) => {
          if (success) {
            this.snackBar.open('Maintenance supprimée avec succès', 'Fermer', {
              duration: 3000
            });
            this.loadMaintenances();
          }
        },
        error: (error) => {
          console.error('Erreur lors de la suppression:', error);
          this.snackBar.open('Erreur lors de la suppression', 'Fermer', {
            duration: 3000
          });
        }
      });
    }
  }

  viewMaintenance(maintenance: Maintenance): void {
    this.dialog.open(MaintenanceDetailDialogComponent, {
      width: '700px',
      data: { maintenance: maintenance }
    });
  }

  getStatusColor(status: MaintenanceStatus): string {
    const colors: { [key: string]: string } = {
      'demande': 'gray',
      'planifiee': 'blue',
      'en_cours': 'orange',
      'terminee': 'green',
      'annulee': 'red',
      'reportee': 'yellow',
      'en_attente_fournisseur': 'purple'
    };
    return colors[status] || 'gray';
  }

  getPriorityColor(priority: Priority): string {
    const colors: { [key: string]: string } = {
      'basse': 'gray',
      'normale': 'blue',
      'haute': 'orange',
      'urgence': 'red'
    };
    return colors[priority] || 'gray';
  }

  getTypeLabel(type: MaintenanceType): string {
    const labels: { [key: string]: string } = {
      'reparation': 'Réparation',
      'entretien': 'Entretien',
      'renovation': 'Rénovation',
      'inspection': 'Inspection',
      'urgence': 'Urgence',
      'maintenance_preventive': 'Maintenance Préventive'
    };
    return labels[type] || type;
  }

  formatPrice(price?: number): string {
    if (!price) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF'
    }).format(price);
  }

  formatDate(date?: Date): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  }

  exportToExcel(): void {
    const dataToExport = this.filteredMaintenances.map(m => ({
      'Référence': m.reference,
      'Bien': m.propertyTitle,
      'Type': this.getTypeLabel(m.type),
      'Titre': m.title,
      'Priorité': m.priority,
      'Statut': m.status,
      'Date prévue': m.scheduledDate,
      'Coût': m.actualCost
    }));
    this.excelService.generateExcel(dataToExport, 'Liste_Maintenances');
  }

  exportToPdf(): void {
    this.pdfService.generatePdf('maintenance-table-container', 'Liste_Maintenances');
  }

  printPage(): void {
    window.print();
  }
}

